import React, { FC, useEffect, useMemo, useCallback } from "react";
import { MediumHeader } from "@narvar/nth-bit-header";
import Panel from "@narvar/nth-block-panel";
import Paragraph from "@narvar/nth-bit-paragraph";
import Button from "@narvar/nth-block-button";
import { ReturnReasonsExchangeProps } from "./types";
import LanguageKeys from "../../ReviewSummary/consts/language_en_US";
import {
  Language,
  Spinner,
  SwatchesList,
  TextLink,
} from "../../ReviewSummary/components";
import { ExchangeVariant, VariantInfo, VariantOption } from "../types";
import Sparsem, {
  IN_STOCK,
  SparsemSearchToken,
  SparsemOption,
} from "../../ReviewSummary/utils/Sparsem";
import { useStyles } from "./ReturnReasonsExchangeStyles";
import { SwatchOption } from "../../ReviewSummary/components/SwatchesList/types";

const tempLanguageKeys = {
  returnsReasonsExchangeTitle: LanguageKeys.returnsReasonsExchangeTitle,
  returnsReasonsExchangeUpsellTitle:
    LanguageKeys.returnsReasonsExchangeUpsellTitle,
  returnsReasonsExchangeUpsellNoThanks:
    LanguageKeys.returnsReasonsExchangeUpsellNoThanks,
  returnsActionsNext: LanguageKeys.returnsActionsNext,
};

const findBestMatch: (
  variants: ExchangeVariant[],
  variantInfo?: { [optionId: string]: { value: string } },
) => ExchangeVariant | undefined = (variants, variantInfo) => {
  const entries = Object.entries(variantInfo ?? {});
  return entries.length
    ? variants.find((variant) =>
        entries.every(
          ([key, optInfo]) =>
            variant.variantInfo?.[key]?.value === optInfo?.value,
        ),
      )
    : undefined;
};

const findExactMatch: (
  variants: ExchangeVariant[],
  variantInfo?: VariantInfo,
) => ExchangeVariant | undefined = (variants, variantInfo) => {
  const bestMatch = findBestMatch(variants, variantInfo);
  if (!bestMatch) return undefined;

  const same = Object.entries(bestMatch.variantInfo ?? {}).every(
    ([key, optInfo]) => variantInfo?.[key]?.value === optInfo.value,
  );
  return same ? bestMatch : undefined;
};

const positionSorterFactory =
  (defaultValue: number) =>
  (a: { position?: number }, b: { position?: number }) =>
    (a.position ?? defaultValue) - (b.position ?? defaultValue);

const sparsemFactory = (
  exchangeVariants: ExchangeVariant[],
  exchangeOptions: VariantOption[],
): Sparsem => {
  const sparsem = new Sparsem();
  // Construct available options' values. Noted that it may not be start from 0
  const optionsSize =
    Math.max(...exchangeOptions.map((o) => o.position ?? -1), -1) + 1 ||
    exchangeOptions.length;
  const opts: SparsemOption[][] = new Array(optionsSize).fill(
    [],
  ) as SparsemOption[][];
  exchangeOptions.forEach((option, i) => {
    const pos = option.position ?? i;
    opts[pos] = option.values.map((v) => v.value);
  });
  // Construct available exchange variants and corresponding option values
  const variants = exchangeVariants
    .filter((variant) => variant.available)
    .map((variant) => {
      const values = Object.values(variant.variantInfo ?? {}).filter(Boolean);
      const ret: SparsemOption[] = new Array(opts.length).fill(
        undefined,
      ) as SparsemOption[];
      values.forEach((info, i) => {
        ret[info.optionPosition ?? i] = info.value;
      });
      return ret;
    });
  sparsem.setArr(variants, opts);
  return sparsem;
};

const ReturnReasonsExchange: FC<ReturnReasonsExchangeProps> = ({
  orderItem,
  upsell,
  shopNow,
  exchangeOptions = [] as VariantOption[],
  exchangeVariants = [] as ExchangeVariant[],
  exchangeSelections,
  loading,
  hideSubmit,
  onSkip,
  onChange,
  onSubmit,
}) => {
  const styles = useStyles();

  const MAIN_EXCHANGE_OPTION_ID = "PRODUCT";
  const {
    returnsReasonsExchangeTitle,
    returnsReasonsExchangeUpsellTitle,
    returnsReasonsExchangeUpsellNoThanks,
    returnsActionsNext,
  } = tempLanguageKeys;

  const sortedExchangeOptions = useMemo<VariantOption[]>(
    () =>
      exchangeOptions
        .sort(positionSorterFactory(exchangeOptions.length))
        .map((option) => ({
          ...option,
          values: option.values.sort(
            positionSorterFactory(option.values.length),
          ),
        })),
    [exchangeOptions],
  );

  const sparsem = useMemo<Sparsem>(
    () => sparsemFactory(exchangeVariants, sortedExchangeOptions),
    [exchangeVariants, sortedExchangeOptions],
  );

  const handleChangeFactory: (opt: VariantOption) => (value: string) => void =
    useCallback(
      (opt) => (value) => {
        // reset (unselect) other options when user clicks on a product
        // (i.e., filter out all fields that are not 'product')
        // toUpperCase() added because this field name is not standardized
        const filteredKeys =
          Object.entries(exchangeSelections || {}).find(
            ([key]) => key.toUpperCase() === MAIN_EXCHANGE_OPTION_ID,
          ) || [];
        const filteredObj = filteredKeys.length
          ? Object.fromEntries([filteredKeys])
          : {};
        const variantInfo =
          opt.id.toUpperCase() === MAIN_EXCHANGE_OPTION_ID
            ? filteredObj
            : { ...exchangeSelections };

        const selected = opt.values.find((v) => v.value === value);
        if (selected) {
          variantInfo[opt.id] = {
            optionId: opt.id,
            optionName: opt.name,
            optionPosition: opt.position,
            ...selected,
          };
        } else {
          delete variantInfo[opt.id];
        }
        const event = {
          exchangeTo: findExactMatch(exchangeVariants, variantInfo),
          variantInfo: variantInfo,
        };
        if (onChange) onChange(event);
      },
      [exchangeSelections, exchangeVariants, onChange],
    );

  const exactMatch: ExchangeVariant | undefined = useMemo(
    () => findExactMatch(exchangeVariants, exchangeSelections),
    [exchangeVariants, exchangeSelections],
  );

  type SwatchesProps = VariantOption & {
    options: SwatchOption[];
  };

  const swatches: Array<SwatchesProps> = useMemo(() => {
    const swatchesOptions = sortedExchangeOptions.map((opt, i) => {
      let values = Object.values(exchangeSelections ?? {});
      values = values
        .map((info, j) => ({
          ...info,
          position: info.optionPosition ?? j,
        }))
        .sort(positionSorterFactory(values.length));
      const searchTokens: SparsemSearchToken[] = new Array(sparsem.num).fill(
        undefined,
      ) as SparsemSearchToken[];
      values.forEach((v, j) => (searchTokens[v?.position ?? j] = v.value));
      searchTokens[opt.position ?? i] = null;
      const availability = sparsem.search(searchTokens);

      const swatchOption: SwatchesProps = {
        ...opt,
        options: opt.values.map((v) => {
          const bestMatchOfCurrentOption = findBestMatch(exchangeVariants, {
            [opt.id]: v,
          });
          return {
            value: v.value,
            label: v.name ?? v.value,
            color: v.color,
            imageUrl: v.imageUrl ?? bestMatchOfCurrentOption?.imageUrl,
            grayedOut: availability[v.value] !== IN_STOCK,
          };
        }),
      };
      return swatchOption;
    });
    return swatchesOptions;
  }, [sparsem, sortedExchangeOptions, exchangeVariants, exchangeSelections]);

  // auto select the first available "product" option during initialization
  useEffect(() => {
    if (upsell) return;
    if (exchangeSelections) return;

    const key = swatches[0]?.id;
    const swatch = /^product$/i.test(key) ? swatches[0] : undefined;
    if (!swatch) return;

    const defaultSelection = swatch.options.find((opt) => !opt.disabled);
    if (defaultSelection) {
      handleChangeFactory(swatch)(defaultSelection.value);
    }
  }, [upsell, swatches, exchangeSelections, handleChangeFactory]);

  const noThanks =
    upsell &&
    Object.values(exchangeSelections ?? {}).filter((d) => d).length === 0;

  const unavailable =
    !loading && exchangeOptions.length === 0 && exchangeVariants.length === 0;

  const handleShopNow = () => {
    const event =
      exchangeVariants.find((v) => v.variantId === orderItem.variantId) ??
      findBestMatch(exchangeVariants, orderItem.variantInfo) ??
      exchangeVariants[0];

    const exchangeTo = { ...event, exchangeForCredit: true };
    onChange?.({
      type: "exchangeForCredit",
      exchangeTo,
      variantInfo: exchangeTo.variantInfo,
    });
    onSubmit?.(exchangeTo);
  };

  const handleSubmit = () => {
    if (exactMatch && exactMatch.available) {
      const event = exactMatch;
      onSubmit?.(event);
    }
  };

  return (
    <>
      <MediumHeader>
        {upsell ? (
          <Language
            langKey="returnsReasonsExchangeUpsellTitle"
            defaultString={returnsReasonsExchangeUpsellTitle}
          />
        ) : (
          <Language
            langKey="returnsReasonsExchangeTitle"
            defaultString={returnsReasonsExchangeTitle}
          />
        )}
      </MediumHeader>
      {!unavailable &&
        swatches.map((opt) => (
          <SwatchesList
            key={opt.id}
            type={opt.displayType}
            label={opt.name}
            options={opt.options}
            value={exchangeSelections?.[opt.id]?.value}
            onChange={handleChangeFactory(opt)}
          />
        ))}
      {unavailable && (
        <Paragraph>
          <Language langKey="returnsReasonsExchangeUnavailable" />
        </Paragraph>
      )}
      {!upsell && shopNow && (
        <Panel header={null}>
          <div className={styles.shopNowWrapper}>
            <Language langKey="returnsReasonsShopNowDescription" />
            <TextLink onClick={handleShopNow}>
              <Language langKey="returnsReasonsShopNowLink" />
            </TextLink>
          </div>
        </Panel>
      )}
      {hideSubmit || unavailable ? null : noThanks ? (
        <Button big onClick={onSkip}>
          <Language
            langKey="returnsReasonsExchangeUpsellNoThanks"
            defaultString={returnsReasonsExchangeUpsellNoThanks}
          />
        </Button>
      ) : (
        <Button big onClick={handleSubmit} disabled={!exactMatch?.available}>
          <Language
            langKey="returnsActionsNext"
            defaultString={returnsActionsNext}
          />
        </Button>
      )}
      {loading && <Spinner />}
    </>
  );
};

export default ReturnReasonsExchange;
